import { Grid } from 'src/design/atoms/Grid';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Checkbox } from 'src/design/atoms/Checkbox';
import React from 'react';
import {
  FiretrolAdminScopes,
  FiretrolJobAdminScopes,
  FiretrolScopes,
  IScope,
  IScopeSection,
  Scope,
} from 'src/data/scopes';

export interface IScopePickerProps {
  scopeList?: IScopeSection[];
  hideLabel?: boolean;
  onChange: (scopes: Scope[]) => void;
  initialSelectedScopes?: Scope[];
}

export const ScopePicker: React.FC<IScopePickerProps> = ({
  scopeList = FiretrolScopes,
  onChange,
  initialSelectedScopes = [],
  hideLabel = false,
}) => {
  const [selectedScopes, setSelectedScopes] = React.useState<Scope[]>(
    initialSelectedScopes,
  );

  React.useEffect(() => {
    onChange(selectedScopes);
  }, [selectedScopes, onChange]);

  const onScopeChecked = (scope: Scope, checked: boolean) => {
    if (checked) {
      return setSelectedScopes((prevState) => [...prevState, scope]);
    }
    setSelectedScopes((prevState) => prevState.filter((v) => v !== scope));
  };

  const renderTags = (items: IScope[], allScope?: string) => {
    return items.map((scope) => {
      const isGlobalAdmin = !!selectedScopes.find((v) => v === 'sys_admin');
      const isChecked = !!selectedScopes.find((v) => v === scope.value);
      const isAll = !!selectedScopes.find((v) => v === allScope);
      const isCurrentAll = scope.value === allScope;
      return (
        <div key={scope.value} style={{ marginBottom: 5 }}>
          <Checkbox
            checked={isChecked || isAll || isGlobalAdmin}
            disabled={
              (isAll && scope.value !== allScope) ||
              (scope.value !== 'sys_admin' && isGlobalAdmin)
            }
            onChange={(event) =>
              onScopeChecked(scope.value, event.currentTarget.checked)
            }
            description={scope.description}
            label={
              isCurrentAll ? (
                <>
                  *{' '}
                  <Text span c="bright" fw={600}>
                    {scope.label}
                  </Text>
                </>
              ) : (
                scope.label
              )
            }
          />
        </div>
      );
    });
  };

  const renderCols = (scopes: IScopeSection[]) => {
    return scopes.map((s) => {
      return (
        <Grid.Col span={{ base: 12, sm: 6, md: 4, lg: 3 }} key={s.title}>
          <Stack gap="xs">
            <Title order={4}>{s.title}</Title>
            {s.description && <Text>{s.description}</Text>}
            {renderTags(s.scopes, s.all_scope)}
          </Stack>
        </Grid.Col>
      );
    });
  };

  return (
    <Stack>
      {!hideLabel && <Title order={3}>Scopes</Title>}
      <Text>
        Scopes determine the specific features users have access to. The data
        inside the modules is controlled by the offices / jobs a user belongs
        to.
      </Text>
      <Grid>{renderCols(FiretrolAdminScopes)}</Grid>
      <Grid>{renderCols(FiretrolJobAdminScopes)}</Grid>
      <Grid>{renderCols(scopeList)}</Grid>
    </Stack>
  );
};
